import React, { useEffect, useState } from "react";
import axios from "axios";
import { 
  Box, Typography, CircularProgress, Alert, Paper, Chip, 
  Dialog, DialogTitle, DialogContent, IconButton, Table, 
  TableHead, TableRow, TableCell, TableBody, Tooltip 
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DynamicTable from "../Dynamic/DynamicTable";
import { useUser } from "../../Context/UserContext";

const FarmerViewStatus = () => {
  const { userId, token } = useUser();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Preview State
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // PDF State
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);

  const getHeaders = () => ({
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  });

  // ✅ FETCH DATA
  const fetchData = async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const res = await fetch(
        `/api/users/farmer-request/?user_id=${userId}`,
        { headers: getHeaders() }
      );
      const response = await res.json();

      const flattenedData = response.results.map((request) => ({
        request_id: request.request_id,
        orderid: request.orderid,
        order_status: request.order_status,
        order_date: request.order_date,
        farmer_name: request.farmer_name,
        mobile_number: request.farmer_mobile,
        village: request.village,
        requested_items: request.requested_items,
      }));

      setData(flattenedData);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [userId]);

  // Cleanup Blob URL to prevent memory leaks
  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  // ✅ PREVIEW HANDLER
  const handlePreview = async (row) => {
    setSelectedOrder(row);
    setPreviewOpen(true);
    setPdfUrl(""); // Reset previous PDF
    setPdfLoading(false);

    // If billed, fetch the PDF
    if (row.order_status === "billed") {
      setPdfLoading(true);
      try {
        const response = await axios.get(
          `/api/users/generate-bill-pdf/?order_id=${row.orderid}`,
          { responseType: "blob" }
        );
        const blob = new Blob([response.data], { type: "application/pdf" });
        setPdfUrl(URL.createObjectURL(blob));
      } catch (err) {
        console.error("PDF fetch error:", err);
        alert("Failed to load bill PDF.");
        setPreviewOpen(false);
      } finally {
        setPdfLoading(false);
      }
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    // Delay revoking so the iframe closes smoothly
    setTimeout(() => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl("");
      }
    }, 150);
  };

  // ✅ TABLE COLUMNS
  const columns = [
    { 
      field: "orderid", 
      headerName: "Order ID",
      render: (row) => <Typography variant="body2" fontWeight={700}>{row.orderid}</Typography>
    },
    {
      field: "order_status",
      headerName: "Status",
      render: (row) => {
        const colors = { 
          pending: "warning", 
          "order-placed": "info", 
          billed: "success", 
          rejected: "error" 
        };
        return (
          <Chip 
            label={row.order_status?.toUpperCase()} 
            color={colors[row.order_status] || "default"} 
            size="small" 
            sx={{ fontWeight: 600, minWidth: 120 }} 
          />
        );
      },
    },
    {
      field: "requested_items",
      headerName: "Items",
      render: (row) => `${row.requested_items?.length || 0} Species`,
    },
    {
      field: "order_date",
      headerName: "Order Date",
      render: (row) =>
        row.order_date ? new Date(row.order_date).toLocaleDateString() : "-",
    },
    {
      field: "actions",
      headerName: "Actions",
      render: (row) => (
        <Box display="flex" gap={1} alignItems="center">
          <Tooltip title={row.order_status === "billed" ? "View Bill" : "View Details"}>
            <IconButton 
              onClick={() => handlePreview(row)} 
              color={row.order_status === "billed" ? "secondary" : "primary"}
            >
              {row.order_status === "billed" ? <PictureAsPdfIcon /> : <VisibilityIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box p={3}>
      <
      >
        {/* <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight={700}>
            📦 My Requests
          </Typography>
        </Box> */}

        {!userId && <Alert severity="warning" sx={{ mb: 2 }}>User not found</Alert>}

        {loading ? (
          <Box textAlign="center" p={5}>
            <CircularProgress />
          </Box>
        ) : (
          <DynamicTable
            data={data}
            columns={columns}
            loading={loading}
            emptyMessage="No Requests Found"
          />
        )}
      </>

      {/* --- PREVIEW DIALOG --- */}
      <Dialog
        open={previewOpen}
        onClose={closePreview}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            height: '85vh',
            display: 'flex',
            flexDirection: 'column',
            m: 2,
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          pb: 1, 
          borderBottom: '1px solid #eee' 
        }}>
          <Typography variant="h6" fontWeight={600}>
            {selectedOrder?.order_status === "billed" ? "🧾 Bill Preview" : `📋 Order Details - ${selectedOrder?.orderid}`}
          </Typography>
          <IconButton onClick={closePreview}>
            ✕
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* RENDER PDF IF BILLED */}
          {selectedOrder?.order_status === "billed" ? (
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
              {pdfLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={50} />
                  <Typography color="text.secondary">Fetching Bill...</Typography>
                </Box>
              ) : pdfUrl ? (
                <iframe 
                  src={`${pdfUrl}#toolbar=0`} 
                  width="100%" 
                  height="100%" 
                  style={{ border: 'none', borderRadius: '4px' }} 
                  title="Bill PDF Preview"
                />
              ) : null}
            </Box>
          ) : (
            /* RENDER TABLE DETAILS IF NOT BILLED */
            <Box sx={{ p: 3, overflowY: 'auto' }}>
              {selectedOrder && (
                <>
                  <Box mb={3} sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Typography variant="body1">
                      <span style={{ color: 'gray' }}>Status: </span> 
                      <b>{selectedOrder.order_status?.toUpperCase()}</b>
                    </Typography>
                    <Typography variant="body1">
                      <span style={{ color: 'gray' }}>Date: </span> 
                      <b>{selectedOrder.order_date ? new Date(selectedOrder.order_date).toLocaleString() : "-"}</b>
                    </Typography>
                    <Typography variant="body1">
                      <span style={{ color: 'gray' }}>Total Items: </span> 
                      <b>{selectedOrder.requested_items?.length}</b>
                    </Typography>
                  </Box>

                  <Table size="small" sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Species</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Requested</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Approved</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedOrder.requested_items?.map((item) => (
                        <TableRow key={item.item_id} hover>
                          <TableCell>
                            {item.species_name} 
                            {item.species_name_tamil && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {item.species_name_tamil}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">{item.requested_quantity}</TableCell>
                          <TableCell align="center">
                            {item.approved_quantity ?? "-"}
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={item.item_status?.toUpperCase()} 
                              size="small" 
                              color={
                                item.item_status === "approved" ? "success" : 
                                item.item_status === "pending" ? "warning" : "error"
                              }
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FarmerViewStatus;